<!--
  =====================================================================
  SECTION FILIALI CARD - BRANCH LOCATION CARD
  =====================================================================
  
  Individual card component for displaying branch/location information.
  
  FEATURES:
  - Fixed height card with image, info, action buttons
  - Vertical bar animation on hover (two-segment split)
  - Detail and Map navigation buttons
  - Address, CAP, city, phone information
  - Google Maps integration with multiple fallback strategies
  - Responsive sizing across breakpoints
  - Image hover zoom effect
  
  BEHAVIOR:
  - Card expands on hover with left-to-right animation
  - Buttons appear from image side
  - Maps priority: Custom URL > coordinates > address search
  - Click on card goes to detail page
  
  =====================================================================
-->

<template>
  <article class="filiale-card">
    <a :href="`/cassa/filiali/${filiale.slug}`">
      
      <div class="vertical-bar"></div>

      <div class="card-inner">
        
        <div v-if="filiale.image" class="card-image">
          <div class="image-container">
            <img :src="filiale.image" :alt="filiale.title" />
          </div>
        </div>
        <div v-else class="card-image-empty"></div>

        <div class="card-content">
          <h3 class="card-title">{{ filiale.title }}</h3>
          <div class="card-info">
            <div class="info-line">
              <span class="info-address">{{ filiale.address }}</span>
            </div>
            <div class="info-line">
              <span class="info-city">{{ filiale.cap }} {{ filiale.city }}</span>
            </div>
            <div class="info-line" v-if="filiale.phone">
              <span class="info-phone">Tel. {{ filiale.phone }}</span>
            </div>
          </div>
        </div>

        <div class="card-actions">
          <button
            class="action-btn action-detail"
            @click.stop="goToDetail(`/cassa/filiali/${filiale.slug}`)"
            :title="`Vai al dettaglio di ${filiale.title}`"
            type="button"
          >
            <img class="icon-img" :src="filiale.iconDetail || '/images/logos/page.png'" alt="Dettaglio" />
            <span class="action-label">Dettaglio</span>
          </button>

          <button
            class="action-btn action-map"
            @click.stop.prevent="openMap(filiale)"
            :title="`Apri mappa per ${filiale.title}`"
            type="button"
          >
            <img class="icon-img" :src="filiale.iconMap || '/images/logos/position.png'" alt="Mappa" />
            <span class="action-label">Mappa</span>
          </button>
        </div>
      </div>
    </a>
  </article>
</template>

<script setup>
// ───────────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Object} filiale - Branch/location data object
 */
defineProps({
  filiale: { type: Object, required: true }
})

// ───────────────────────────────────────────────────────────────────
// NAVIGATION HANDLERS
// ───────────────────────────────────────────────────────────────────

/**
 * Navigates to branch detail page
 * @param {String} url - Detail page URL
 */
function goToDetail(url) {
  window.location.assign(url)
}

// ───────────────────────────────────────────────────────────────────
// MAP INTEGRATION
// ───────────────────────────────────────────────────────────────────

/**
 * Opens Google Maps with branch location
 * Priority: Custom URL > coordinates > address search
 * @param {Object} filiale - Branch data with location info
 */
function openMap(filiale) {
  // Priority 1: Custom Google Maps URL from JSON
  if (filiale?.mapsUrl) {
    window.open(filiale.mapsUrl, '_blank', 'noopener,noreferrer')
    return
  }
  
  // Priority 2: Latitude/longitude coordinates
  if (filiale?.coordinates && typeof filiale.coordinates.lat === 'number' && typeof filiale.coordinates.lng === 'number') {
    const { lat, lng } = filiale.coordinates
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }
  
  // Fallback: Address search
  const dest = encodeURIComponent(`${filiale?.address || ''} ${filiale?.city || ''}`.trim())
  const url = `https://www.google.com/maps/search/?api=1&query=${dest}`
  window.open(url, '_blank', 'noopener,noreferrer')
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";

/* ------------------------------------------------------------------
   Card base styling
------------------------------------------------------------------ */
.filiale-card {
  position: relative;
  display: block;
  background: $surface-base;
  color: $text-color;
  box-shadow: 0 2px 4px rgba($clr-brand-dark, 0.05);
  border: 1px solid $clr-border;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  height: 180px; 

  a {
    text-decoration: none;
    color: inherit;
    display: block;
    position: relative;
    height: 100%;
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba($clr-brand-dark, 0.12);
      border-color: rgba($clr-brand-dark, 0.2);
    }
  }

  /* Animated vertical bar with two-segment split */
  .vertical-bar {
    position: absolute;
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    pointer-events: none;
    z-index: 2;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 3px;
      background: $clr-border;
      height: 0;
      transition: height 0.5s ease, background 0.5s ease;
    }

    &::before { bottom: 50%; }
    &::after  { top: 50%; }
  }

  @media (hover: hover) {
    &:hover .vertical-bar::before,
    &:hover .vertical-bar::after {
      height: 50%;
      background: $clr-tertiary;
    }

    &:hover .card-image img {
      transform: scale(1.05);
    }
  }

  /* Card layout */
  .card-inner {
    display: flex;
    align-items: center;
    height: 100%;
  }

  /* Image container */
  .card-image,
  .card-image-empty {
    flex: 0 0 280px;
    height: 100%;
    position: relative;
  }
  
  .card-image {
    overflow: hidden;
    background: $clr-bg-light;  

    .image-container {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.45s ease;
      }
    }
  }
  
  .card-image-empty {
    background: $clr-bg-light;
  }

  /* Text content */
  .card-content {
    flex: 1;
    padding: $space-2 $space-2-5;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
  }

  .card-title {
    font-size: clamp(1.4rem, 2.5vw, 1.8rem);
    margin: 0 0 $space-1;
    font-weight: 600;
    line-height: 1.2;
    font-family: 'Karla', sans-serif;
    color: $text-color;
    overflow: hidden;
    text-overflow: ellipsis;
    @include line-clamp(2, 1.2, $overflow: false);
  }

  .card-info {
    display: flex;
    flex-direction: column;
    @include flex-gap($space-0-25, column);
  }

  .info-line {
    font-size: clamp(0.95rem, 1.6vw, 1.15rem);
    color: $clr-brand-gray;
    line-height: 1.4;
    
    .info-address { font-weight: 500; }
    .info-city { opacity: 0.85; }
    .info-phone { color: $clr-tertiary; font-weight: 600; }
  }

  /* Action buttons */
  .card-actions {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    @include flex-gap(0, column);
    padding: 0;
    z-index: 3;
    height: 100%;
    align-items: stretch;
    justify-content: stretch;
  }

  .action-btn {
    width: 64px;
    flex: 1 1 50%;
    height: auto;
    background: $surface-base;
    border: none;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    @include flex-gap($space-0-25, column);
    cursor: pointer;
    color: $clr-brand-gray;

    transition:
      background-color 0.85s cubic-bezier(.22,.61,.36,1),
      color 0.85s ease,
      transform 0.85s cubic-bezier(.22,.61,.36,1);

    .icon-img {
      width: 20px;
      height: 20px;
      display: block;
      transition: transform 0.85s cubic-bezier(.22,.61,.36,1), filter 0.85s ease;
      filter: invert(12%) sepia(6%) saturate(300%) hue-rotate(169deg) brightness(95%) contrast(90%);
    }
    
    .action-label {
      line-height: 1;
      text-align: center;
      white-space: nowrap;
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.4px;
      opacity: 0;
      transform: translateY(6px);
      transition: opacity 0.95s ease, transform 0.95s ease, color 0.95s ease;
      color: $clr-brand-gray;
    }

    @media (hover: hover) {
      &:hover {
        background-color: $clr-tertiary;
        color: $clr-white;
        transform: translateY(-1px);
        .icon-img { transform: scale(1.12); filter: brightness(0) invert(1); }
        .action-label { color: $clr-white; opacity: 1; transform: translateY(0); }
      }
    }

    &:active { transform: translateY(0); }
  }
}

/* ------------------------------------------------------------------
   Responsive breakpoints
------------------------------------------------------------------ */
@media (max-width: 1700px) {
  .filiale-card {
    height: 170px;
    .card-image,
    .card-image-empty { flex: 0 0 260px; }
    .card-content { padding: $space-1-75 $space-1-5; }
    .card-title { font-size: clamp(1.3rem, 2.3vw, 1.7rem); }
  }
}

@media (max-width: 1400px) {
  .filiale-card {
    height: 160px;
    .card-image,
    .card-image-empty { flex: 0 0 240px; }
    .card-content { padding: $space-1-25 $space-1; }
    .card-title { font-size: clamp(1.2rem, 2.2vw, 1.6rem); margin-bottom: $space-0-75; }
    .info-line { font-size: clamp(0.9rem, 1.5vw, 1.1rem); }
    .card-actions .action-btn { width: 40px; height: auto; }
  }
}

@media (max-width: 1300px) {
  .filiale-card {
    height: 150px;
    .card-image,
    .card-image-empty { flex: 0 0 220px; }
    .card-content { padding: $space-1 $space-0-75; }
    .card-title { font-size: clamp(1.1rem, 2vw, 1.4rem); margin-bottom: $space-0-5; }
    .info-line {
      font-size: clamp(0.85rem, 1.4vw, 1rem);
      &:last-child { display: none; }
    }
    .card-actions {
      padding: 0 $space-1;
      .action-btn {
        width: 36px;
        height: auto;
        .icon-img { width: 18px; height: 18px; }
      }
    }
  }
}

@media (max-width: 920px) {
  .filiale-card {
    height: 140px;
    .card-image,
    .card-image-empty { flex: 0 0 200px; }
    .card-content { padding: $space-1 $space-0-75; }
    .card-title { font-size: clamp(1.1rem, 2.5vw, 1.4rem); font-weight: 600; }
    .card-actions { padding: 0 $space-0-75; }
    .card-actions .action-btn { width: 34px; height: auto; }
  }
}

@media (max-width: 480px) {
  .filiale-card {
    height: auto;
    .card-inner { 
      flex-direction: row; 
      flex-wrap: wrap; 
    }
    .card-image,
    .card-image-empty { 
      flex: 0 0 100%; 
      width: 100%; 
      height: 200px; 
    }
    .card-content { 
      flex: 1; 
      padding: $space-1-25 $space-1; 
      min-width: 0;
    }
    .card-actions { 
      flex: 0 0 64px; 
      flex-direction: column; 
      height: auto; 
      align-self: stretch;
      padding: 0;
    }
    .card-actions .action-btn { 
      width: 100%; 
      flex: 1; 
    }
  }
}
</style>